import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  accionsApi,
  BLOCS,
  esImatge,
  useAccions,
  useCircuit,
  useDocuments,
  type AccioDto,
  type ActivacioDto,
  type DestinatariDto,
} from '@/api/activacions';
import { useMissatgeError } from '@/api/errors';
import { Icona, type NomIcona } from '@/components/Icona';
import { useFormat } from '@/i18n/useFormat';
import { useMe } from '@/permisos/MeContext';
import { Avatar, BadgeArea, BadgeEstat, Buit, IconaEstatDestinatari } from './comu';
import { etiquetaTipusDesti, textAccio, useReenviaPendents } from './suport';
import { TextMissatge } from './W04Xat';

function Kv({ k, children, bloc }: { k: string; children: ReactNode; bloc?: boolean }) {
  const buit = children == null || children === '' || children === false;
  return (
    <div className={`kv${bloc ? ' kv--bloc' : ''}`}>
      <span className="kv__k">{k}</span>
      <span className={`kv__v${buit ? ' dim' : ''}`}>{buit ? '—' : children}</span>
    </div>
  );
}

export function PanellDades({
  a,
  destinataris,
}: {
  a: ActivacioDto;
  destinataris: DestinatariDto[];
}) {
  const { t } = useTranslation('operacio');
  const tc = useTranslation('comu').t;
  const f = useFormat();
  const c = (clau: string) => t(`detall.camps.${clau}`);
  const confirmats = destinataris.filter((d) => d.estat === 'CONFIRMAT').length;
  const pendents = destinataris.filter(
    (d) => d.estat !== 'CONFIRMAT' && d.estat !== 'EN_ESPERA' && d.estat !== 'NO_NOTIFICAT',
  ).length;
  return (
    <div className="panel" data-panel="dades">
      <div className="panel__head">
        <h3>{t('detall.panells.dades')}</h3>
      </div>
      <div className="panel__body">
        <Kv k={c('codi')}>
          <span className="mono">{a.codi}</span>
        </Kv>
        <Kv k={c('tipus')}>
          {a.critica ? tc('tipusActivacio.CRITICA') : tc(`tipusActivacio.${a.tipus}`)}
        </Kv>
        <Kv k={c('titol')}>{a.titol}</Kv>
        <Kv k={c('descripcio')} bloc>
          {a.descripcio}
        </Kv>
        <Kv k={c('proves')} bloc>
          {a.provesRealitzades}
        </Kv>
        {a.risc ? (
          <Kv k={c('risc')} bloc>
            {a.risc}
          </Kv>
        ) : null}
        {a.perills ? (
          <Kv k={c('perills')} bloc>
            {a.perills}
          </Kv>
        ) : null}
        {a.mesures ? (
          <Kv k={c('mesures')} bloc>
            {a.mesures}
          </Kv>
        ) : null}
        <Kv k={c('inici')}>{a.iniciIncident ? f.dataHora(a.iniciIncident) : null}</Kv>
        <Kv k={c('obert')}>{f.dataHora(a.createdAt)}</Kv>
        <Kv k={c('activador')}>{a.activador}</Kv>
        <Kv k={c('contacte')}>{a.contacteText}</Kv>
        <Kv k={c('zona')}>{a.zona ?? a.zonaText}</Kv>
        <Kv k={c('area')}>
          {a.area ? (
            <>
              {a.area} <BadgeArea tipus={a.areaTipus} />
            </>
          ) : null}
        </Kv>
        <Kv k={c('departament')}>{a.departament}</Kv>
        <Kv k={c('servei')}>{a.servei}</Kv>
        <Kv k={c('canals')}>{a.canals.map((x) => tc(`canal.${x}`)).join(', ')}</Kv>
        <Kv k={c('estat')}>
          <BadgeEstat estat={a.estat} />
        </Kv>
        <Kv k={c('itcau')}>
          {a.itcau?.ref ? (
            a.itcau.url ? (
              <a className="mono c-blue" href={a.itcau.url} target="_blank" rel="noreferrer">
                {a.itcau.ref}
              </a>
            ) : (
              <span className="mono">{a.itcau.ref}</span>
            )
          ) : null}
        </Kv>
        <Kv k={c('confirmats')}>
          <span className="c-green">
            {t('detall.deTotal', { valor: confirmats, total: destinataris.length })}
          </span>
        </Kv>
        <Kv k={c('pendents')}>
          <span className={pendents > 0 ? 'c-red' : undefined}>{pendents}</span>
        </Kv>
        <Kv k={c('primeraConfirmacio')}>
          {a.primeraConfirmacioAt ? f.dataHora(a.primeraConfirmacioAt) : null}
        </Kv>
        <Kv k={c('tancada')}>{a.tancament?.at ? f.dataHora(a.tancament.at) : null}</Kv>
        <Kv k={c('motiuTancament')}>
          {a.tancament?.motiu ? tc(`estatActivacio.${a.tancament.motiu}`) : null}
        </Kv>
        {a.tancament?.comentari ? (
          <Kv k={c('comentari')} bloc>
            {a.tancament.comentari}
          </Kv>
        ) : null}
      </div>
    </div>
  );
}

const ICONA_ACCIO: Partial<Record<string, [NomIcona, string]>> = {
  CREADA: ['exclamationmark-triangle', ''],
  ENVIADA: ['paperplane', ''],
  REENVIADA: ['arrow-clockwise', ''],
  CONFIRMADA: ['checkmark', 'ok'],
  ACCEPTADA: ['hand-raised', 'ok'],
  TANCADA: ['checkmark-circle', 'ok'],
  EXPERT_ACTIVAT: ['person-check', 'incident'],
  ESCALADA: ['arrow-up', 'incident'],
  ITCAU_VINCULAT: ['link', 'crisi'],
  NOTA: ['pencil', ''],
};

function EvAccio({ ac }: { ac: AccioDto }) {
  const { t } = useTranslation('operacio');
  const f = useFormat();
  const [icona, to] = ICONA_ACCIO[ac.tipus] ?? (['clock', ''] as [NomIcona, string]);
  return (
    <li className="ev" data-accio={ac.tipus}>
      <span className={`ev__punt${to ? ` ev__punt--${to}` : ''}`}>
        <Icona nom={icona} />
      </span>
      <div className="ev__cos">
        <span>
          <strong>
            {t(`detall.accioTitol.${ac.tipus}`, { defaultValue: t('detall.accioTitol.ALTRA') })}
          </strong>{' '}
          · {textAccio(ac, t)}
          {typeof ac.detall?.text === 'string' && ac.tipus !== 'NOTA' ? ` · ${ac.detall.text}` : ''}
        </span>
        {ac.tipus === 'NOTA' && typeof ac.detall?.text === 'string' && ac.detall.text ? (
          <div className="ev__nota">
            <TextMissatge text={ac.detall.text} />
          </div>
        ) : null}
        <span className="dim t-caption">{f.hora(ac.at)}</span>
      </div>
    </li>
  );
}

/**
 * Pestanya Activitat del detall: només la cronologia d'accions i esdeveniments (creació,
 * enviaments, confirmacions, escalats, notes…). La conversa i les imatges són a la pestanya Xat.
 */
export function PanellActivitat({ a }: { a: ActivacioDto }) {
  const { t } = useTranslation('operacio');
  const accions = useAccions(a.id);
  const esdeveniments = (accions.data ?? [])
    .filter((ac) => !esImatge(ac))
    .sort((x, y) => x.at.localeCompare(y.at));
  return (
    <div className="activitat" data-panel="activitat">
      <ol
        className="activitat__llista"
        aria-live="polite"
        aria-label={t('detall.pestanyes.activitat')}
      >
        {esdeveniments.length === 0 ? (
          <li>
            <Buit icona="clock">{t('detall.senseActivitat')}</Buit>
          </li>
        ) : (
          esdeveniments.map((ac) => <EvAccio key={ac.id} ac={ac} />)
        )}
      </ol>
    </div>
  );
}

function horaEstat(d: DestinatariDto): string | null | undefined {
  return d.confirmatAt ?? d.llegitAt ?? d.lliuratAt ?? d.enviatAt;
}

export function PanellDestinataris({
  a,
  destinataris,
  onAfegeix,
}: {
  a: ActivacioDto;
  destinataris: DestinatariDto[];
  onAfegeix?: () => void;
}) {
  const { t } = useTranslation('operacio');
  const tc = useTranslation('comu').t;
  const f = useFormat();
  const missatgeError = useMissatgeError();
  const { pendents, reenvia, potReenviar } = useReenviaPendents(a, destinataris);
  return (
    <div className="panel" data-panel="destinataris">
      <div className="panel__head">
        <h3>{t('detall.panells.destinataris')}</h3>
        <div className="spacer" />
        <span className="t-caption-2 dim">
          {t('confirmacions', {
            confirmats: destinataris.filter((d) => d.estat === 'CONFIRMAT').length,
            total: destinataris.length,
          })}
        </span>
        {onAfegeix ? (
          <button type="button" className="btn btn--s" onClick={onAfegeix}>
            <Icona nom="plus" />
            {t('detall.accions.afegeixDestinataris')}
          </button>
        ) : null}
      </div>
      {BLOCS.map((bloc) => {
        const delBloc = destinataris.filter((d) => d.bloc === bloc);
        if (!delBloc.length) return null;
        return (
          <div key={bloc} data-bloc={bloc}>
            <div className="list-title">
              {tc(`blocDestinatari.${bloc}`)} · {delBloc.length}
            </div>
            {delBloc.map((d) => {
              const hora = horaEstat(d);
              return (
                <div className="list-row" key={d.id}>
                  <Avatar nom={d.nom} />
                  <div className="list-row__text">
                    <div className="list-row__label">{d.nom}</div>
                    <div className="list-row__sub">
                      {tc(`estatDestinatari.${d.estat}`)}
                      {hora ? ` ${f.hora(hora)}` : ''}
                      {d.nivell > 1 ? ` · ${t('detall.nivell', { nivell: d.nivell })}` : ''}
                      {d.acceptat ? ` · ${t('detall.haAcceptat')}` : ''}
                    </div>
                  </div>
                  <IconaEstatDestinatari estat={d.estat} />
                </div>
              );
            })}
          </div>
        );
      })}
      {destinataris.length === 0 ? (
        <Buit icona="person-2">{t('detall.senseDestinataris')}</Buit>
      ) : null}
      {potReenviar ? (
        <div className="panel__foot">
          <span className="t-caption dim spacer">{t('detall.reenviarText')}</span>
          <button
            type="button"
            className="btn btn--xs"
            disabled={reenvia.isPending}
            onClick={() => reenvia.mutate(undefined)}
          >
            <Icona nom="arrow-clockwise" />
            {t('detall.accions.reenvia', { count: pendents.length })}
          </button>
        </div>
      ) : null}
      {reenvia.isSuccess ? (
        <p className="list-foot" role="status">
          {t('detall.reenviat', { count: reenvia.data.enviaments })}
        </p>
      ) : null}
      {reenvia.error ? (
        <p className="error-text list-foot" role="alert">
          {missatgeError(reenvia.error)}
        </p>
      ) : null}
    </div>
  );
}

const COLOR_NIVELL = { fet: 'tl-item--green', actual: 'tl-item--orange', pendent: '' } as const;

export function PanellCadenaEscalat({
  a,
  destinataris,
}: {
  a: ActivacioDto;
  destinataris: DestinatariDto[];
}) {
  const { t } = useTranslation('operacio');
  const tc = useTranslation('comu').t;
  const { pot } = useMe();
  const circuit = useCircuit(a.serveiIds[0], pot('cai.circuits', 'CONSULTAR'));
  const nivells = circuit.data?.nivells?.length
    ? [...circuit.data.nivells].sort((x, y) => x.ordre - y.ordre)
    : null;
  const perNivell = new Map<number, DestinatariDto[]>();
  for (const d of destinataris) perNivell.set(d.nivell, [...(perNivell.get(d.nivell) ?? []), d]);
  const nivellActual = Math.max(1, a.nivellEscalat);
  const estatNivell = (ordre: number) =>
    ordre < nivellActual ? 'fet' : ordre === nivellActual ? 'actual' : 'pendent';
  const noms = (ordre: number) => (perNivell.get(ordre) ?? []).map((d) => d.nom).join(', ');
  let acumulat = 0;
  return (
    <div className="panel" data-panel="escalat">
      <div className="panel__head">
        <h3>{t('detall.panells.escalat')}</h3>
        <div className="spacer" />
        {circuit.data ? <span className="t-caption-2 dim">{circuit.data.nom}</span> : null}
      </div>
      <div className="panel__body">
        {nivells || perNivell.size ? (
          <ol className="timeline">
            {nivells
              ? nivells.map((n) => {
                  acumulat += n.esperaSeg;
                  const estat = estatNivell(n.ordre);
                  return (
                    <li
                      key={n.ordre}
                      className={`tl-item ${COLOR_NIVELL[estat]}`}
                      data-nivell={estat}
                    >
                      <div className="tl-time">
                        {t('detall.nivellMinuts', {
                          nivell: n.ordre,
                          minuts: Math.round(acumulat / 60),
                        })}
                      </div>
                      <div className="tl-title">{etiquetaTipusDesti(n.tipusDesti, t)}</div>
                      <div className="tl-text">
                        {[
                          noms(n.ordre),
                          n.canals.map((c) => tc(`canal.${c}`)).join(', '),
                          t(`detall.estatNivell.${estat}`),
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    </li>
                  );
                })
              : [...perNivell.entries()]
                  .sort(([x], [y]) => x - y)
                  .map(([nivell, ds]) => {
                    const estat = estatNivell(nivell);
                    return (
                      <li
                        key={nivell}
                        className={`tl-item ${COLOR_NIVELL[estat]}`}
                        data-nivell={estat}
                      >
                        <div className="tl-time">{t('detall.nivell', { nivell })}</div>
                        <div className="tl-title">
                          {[...new Set(ds.map((d) => tc(`blocDestinatari.${d.bloc}`)))].join(', ')}
                        </div>
                        <div className="tl-text">
                          {ds.map((d) => d.nom).join(', ')} · {t(`detall.estatNivell.${estat}`)}
                        </div>
                      </li>
                    );
                  })}
          </ol>
        ) : (
          <Buit icona="arrow-up">{t('detall.senseCircuit')}</Buit>
        )}
      </div>
    </div>
  );
}

export function PanellDocumentacio({ a }: { a: ActivacioDto }) {
  const { t } = useTranslation('operacio');
  const f = useFormat();
  const documents = useDocuments(a.id);
  const missatgeError = useMissatgeError();
  const [error, setError] = useState<unknown>(null);
  const descarrega = async (id: number, nom: string) => {
    setError(null);
    try {
      await accionsApi.document(a.id, id, nom);
    } catch (e) {
      setError(e);
    }
  };
  const docs = documents.data ?? [];
  return (
    <div className="panel" data-panel="documentacio">
      <div className="panel__head">
        <h3>{t('detall.panells.documentacio')}</h3>
        <div className="spacer" />
        <span className="t-caption-2 dim">{docs.length}</span>
      </div>
      {docs.length === 0 ? (
        <Buit icona="doc-text">{t('detall.senseDocuments')}</Buit>
      ) : (
        docs.map((d) => (
          <div className="list-row" key={d.id}>
            <div className="list-row__icon bg-blue">
              <Icona nom="doc-text" />
            </div>
            <div className="list-row__text">
              <div className="list-row__label">{d.titol}</div>
              <div className="list-row__sub">
                {[
                  d.codi,
                  d.versioVigent != null ? t('detall.versio', { versio: d.versioVigent }) : null,
                  d.updatedAt ? f.data(d.updatedAt) : null,
                  d.responsable,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            </div>
            <button
              type="button"
              className="btn btn--ghost btn--xs btn--icona"
              disabled={!d.descarregable}
              aria-label={t('detall.accions.obre')}
              title={t('detall.accions.obre')}
              onClick={() => void descarrega(d.id, d.titol)}
            >
              <Icona nom="eye" />
            </button>
          </div>
        ))
      )}
      <div className="list-foot">
        <Icona nom="lock" /> {t('detall.documentsNota', { servei: a.servei ?? '—' })}
      </div>
      {error ? (
        <p className="error-text list-foot" role="alert">
          {missatgeError(error)}
        </p>
      ) : null}
    </div>
  );
}
